import { useInfiniteQuery } from '@tanstack/react-query'
import { Check, Image as ImageIcon, ImageOff, ImagePlus, X } from 'lucide-react'
import { useState } from 'react'

import { api } from '../../api/client'
import { Button, ICON_STROKE, Skeleton, cx } from '../../ui'
import type { ProjectAsset } from '../../api/types.gen'

interface Props {
  projectId: string
  /** Id del asset elegido, o null para «sin portada». */
  value: string | null
  /**
   * Miniatura de la portada con la que se llega, resuelta por el servidor.
   *
   * La usa la pantalla de editar: sin ella habría que cargar las fotos sólo
   * para enseñar cuál es la portada actual, y aun así no aparecería si cayera
   * fuera de la primera página.
   */
  initialThumbUrl?: string | null
  onChange: (assetId: string | null) => void
}

interface AssetPage {
  data: ProjectAsset[]
  next_cursor: string | null
}

/** Lo que cabe pedir de una vez sin que la retícula tarde en pintar. */
const PAGE_SIZE = 60

/**
 * Elegir con qué foto abre la entrega.
 *
 * Empieza plegado, y es deliberado: el diálogo ya pregunta cinco cosas y la
 * portada es la única opcional que necesita mirar 400 miniaturas. Desplegado
 * abre su propia zona de scroll en lugar de estirar el formulario, para que
 * «Crear enlace» no se vaya a tres pantallas de distancia.
 *
 * Sólo se pueden elegir fotos y vídeos que ya tengan miniatura. Un RAW recién
 * subido todavía no la tiene, y ofrecer como portada algo que el estudio no
 * puede ver —y que el portal no sabría pintar— sería prometer una imagen que
 * saldría en blanco.
 */
export function CoverPicker({ projectId, value, initialThumbUrl, onChange }: Props) {
  const [open, setOpen] = useState(false)

  const { data, isPending, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['project-assets', projectId],
      queryFn: ({ pageParam }) =>
        api.get<AssetPage>(
          `/projects/${projectId}/assets?limit=${PAGE_SIZE}` +
            (pageParam === null ? '' : `&cursor=${encodeURIComponent(pageParam)}`),
        ),
      initialPageParam: null as string | null,
      getNextPageParam: (last: AssetPage) => last.next_cursor,
      // No se piden hasta abrir el selector: la mayoría de enlaces se crean sin
      // portada, y cada página son 60 URLs firmadas. Para enseñar la portada
      // con la que se llega no hace falta la lista — viene ya resuelta.
      enabled: open,
    })

  const assets = data?.pages.flatMap((page) => page.data) ?? []
  const usable = assets.filter(
    (asset) => asset.thumb_url !== null && (asset.kind === 'image' || asset.kind === 'video'),
  )
  const chosen = usable.find((asset) => asset.id === value) ?? null

  /*
   | La miniatura de lo elegido, venga de donde venga.
   |
   | Si el estudio acaba de escoger una, sale de la lista que tiene delante. Si
   | llega con una puesta de antes —editar un enlace— sale de lo que resolvió el
   | servidor, que es lo único que la encuentra cuando cae fuera de la primera
   | página. Sólo se cae al icono cuando la foto ya no se puede pintar.
   */
  const chosenThumbUrl = chosen?.thumb_url ?? initialThumbUrl ?? null

  return (
    <div className="flex flex-col gap-2">
      <span className="text-label font-medium text-ink">Portada (opcional)</span>

      {value !== null ? (
        <div className="flex items-center gap-3 rounded-card border border-line p-2">
          {chosenThumbUrl !== null ? (
            <img
              src={chosenThumbUrl}
              alt=""
              className="h-14 w-14 shrink-0 rounded-control object-cover"
            />
          ) : (
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-control bg-surface-sunken text-ink-subtle">
              <ImageIcon size={18} strokeWidth={ICON_STROKE} aria-hidden />
            </span>
          )}

          {/* El nombre sólo cuando se sabe: al editar, la portada puede estar
              fuera de la página cargada y entonces tenemos su miniatura pero no
              su fila. Enseñar la foto y llamarla «la portada» es honesto; poner
              un nombre inventado, no. */}
          <p className="min-w-0 flex-1 truncate text-body text-ink-muted">
            {chosen?.filename ?? 'La portada de esta entrega'}
          </p>
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Quitar la portada"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-control text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
          </button>
        </div>
      ) : (
        <p className="text-caption text-ink-subtle">
          La foto con la que abre la galería de este cliente. Sin ella, la entrega empieza
          directamente por la retícula.
        </p>
      )}

      {!open ? (
        <div>
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            <ImagePlus size={16} strokeWidth={ICON_STROKE} aria-hidden />
            {value === null ? 'Elegir portada' : 'Cambiar'}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Zona de scroll propia, acotada a unas tres filas: con 400 fotos, una
              retícula sin tope empuja el resto del formulario fuera de la vista. */}
          <div className="max-h-56 overflow-y-auto rounded-card border border-line p-1.5">
            {isPending ? (
              <ul className="grid grid-cols-4 gap-1.5 sm:grid-cols-5">
                {Array.from({ length: 10 }, (_, index) => (
                  <li key={index}>
                    <Skeleton className="aspect-square w-full rounded-control" />
                  </li>
                ))}
              </ul>
            ) : error !== null ? (
              <p className="p-4 text-center text-body text-ink-muted">
                No se pudieron cargar tus fotos. Puedes crear el enlace sin portada.
              </p>
            ) : usable.length === 0 ? (
              <p className="flex flex-col items-center gap-2 p-4 text-center text-body text-ink-muted">
                <ImageOff size={20} strokeWidth={ICON_STROKE} aria-hidden />
                {/*
                  Dos vacíos distintos, porque llevan a acciones distintas: sin
                  archivos hay que subir; con archivos y sin miniaturas sólo hay
                  que esperar a que el worker termine.
                */}
                {assets.length === 0
                  ? 'Todavía no has subido nada a este proyecto.'
                  : 'Las miniaturas se están generando. Vuelve en un momento y podrás elegir.'}
              </p>
            ) : (
              <ul className="grid grid-cols-4 gap-1.5 sm:grid-cols-5">
                {usable.map((asset) => {
                  const selected = asset.id === value

                  return (
                    <li key={asset.id}>
                      <button
                        type="button"
                        // Volver a tocar la elegida la quita: es la forma de
                        // deshacer sin buscar el aspa de arriba.
                        onClick={() => onChange(selected ? null : asset.id)}
                        aria-pressed={selected}
                        aria-label={`Usar ${asset.filename} de portada`}
                        className={cx(
                          'relative block aspect-square w-full overflow-hidden rounded-control bg-surface-sunken',
                          // Hacia dentro, como en la galería del portal: hacia
                          // fuera se comería el hueco entre celdas.
                          selected &&
                            'outline outline-2 outline-accent-media [outline-offset:-2px]',
                        )}
                      >
                        <img
                          src={asset.thumb_url ?? undefined}
                          alt={asset.filename}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover"
                        />

                        {selected && (
                          // Encima de una foto el tema no vale: el fondo es la
                          // imagen. Por eso la variante `-media`, calculada
                          // contra fondos arbitrarios.
                          <span className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-accent-media text-accent-media-contrast shadow-raised">
                            <Check size={13} strokeWidth={2.5} aria-hidden />
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                busy={isFetchingNextPage && 'Cargando…'}
                onClick={() => void fetchNextPage()}
              >
                Ver más fotos
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
